import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const provinceKeys = {
  all: ['provinces'],
  lists: () => [...provinceKeys.all, 'list'],
  details: () => [...provinceKeys.all, 'detail'],
  detail: (id) => [...provinceKeys.details(), id],
};

/**
 * Hook para obtener todas las provincias
 */
export const useProvinces = () => {
  return useQuery({
    queryKey: provinceKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('provinces');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una provincia específica
 * @param {string} id - UUID de la provincia
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProvince = (id, options = {}) => {
  return useQuery({
    queryKey: provinceKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`provinces/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva provincia
 */
export const useCreateProvince = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (provinceData) => {
      const response = await apiClient.post('provinces', provinceData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: provinceKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating province:', error);
    },
  });
};

/**
 * Hook para actualizar una provincia existente
 */
export const useUpdateProvince = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (provinceData) => {
      const response = await apiClient.put(`provinces/${provinceData.uuid}`, provinceData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(provinceKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: provinceKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating province:', error);
    },
  });
};

/**
 * Hook para eliminar una provincia
 */
export const useDeleteProvince = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`provinces/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: provinceKeys.lists() });
      queryClient.removeQueries({ queryKey: provinceKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting province:', error);
    },
  });
};
